import {
  DayOfWeek,
  DocumentReference,
  INamedDocument,
  ISoftDelete,
  Time24hrType,
  Timestamp,
  isObject,
  type IFirestoreModel,
  type Timezone,
} from '@principle-theorem/shared';
import { isString } from 'lodash';
import { IAddressMetadata } from '../address/address';
import { IAIReceptionistAnalyticsConfig } from '../ai-receptionist/analytics-config';
import { IBridgeDevice } from '../bridge-device/bridge-device';
import { IFeeSchedule } from '../clinical-charting/fees/fee-schedule/fee-schedule';
import { EventType } from '../event/event';
import { IPrintPrescriptionSettings } from '../prescription/prescription';
import { IDocumentSettings } from '../printing/document-settings';
import { type IRoster } from '../roster';
import { MohAgreementHolderSource } from '../transaction/moh/moh-agreement-holder';
import { IBillingEntity, IStaffer } from '../staffer/staffer';
import { ITemplateDefinition } from '../templating/template-definition';
import {
  type PracticeTimelineHourIncrement,
  type PracticeTimelineTooltipDelay,
} from '../timeline/interactive-timeline-display-options';
import { IUser } from '../user/user';
import {
  IStatusIndicatorConfig,
  StatusIndicatorType,
} from '../timeline/status-indicators';

export enum PracticeCollection {
  CallEvents = 'callEvents',
  AIReceptionistKnowledgeBase = 'aiReceptionistKnowledgeBase',
  AIReceptionistSettings = 'aiReceptionistSettings',
  MediaTags = 'tags',
  Tasks = 'tasks',
  LabJobs = 'labJobs',
  Contacts = 'contacts',
  FundingRequests = 'fundingRequests',
  Products = 'products',
  Equipment = 'equipment',
  Instruments = 'instruments',
  Consumables = 'consumables',
  FeeSchedules = 'feeSchedules',
  SterilisationRecords = 'sterilisationRecords',
  SterilisationCycles = 'sterilisationCycles',
  SchedulingAlerts = 'schedulingAlerts',
  RecurringTaskConfigurations = 'recurringTaskConfigurations',
  TyroTerminals = 'tyroTerminals',
  SmartpayTerminals = 'smartpayTerminals',
  VerifoneTerminals = 'verifoneTerminals',
  BridgeDevices = 'bridgeDevices',
  ScheduleSummaries = 'scheduleSummaries',
  HicapsConnectTerminals = 'hicapsConnectTerminals',
  HicapsConnectProcesses = 'hicapsConnectProcesses',
  HicapsConnectTerminalsV2 = 'hicapsConnectTerminalsV2',
  HicapsConnectMerchants = 'hicapsConnectMerchants',
  SchedulingEventSummaries = 'schedulingEventSummaries',
  SetupWizard = 'setupWizard',
  AIReceptionistWebsiteSnapshots = 'aiReceptionistWebsiteSnapshots',
  DepositPaymentAttempts = 'depositPaymentAttempts',
  DepositRequests = 'depositRequests',
}

export enum LabJobIncomeTimestampField {
  SentAt = 'sentAt',
  ReceivedAt = 'receivedAt',
  IssuedAt = 'issuedAt',
}

export interface IPracticeSettings {
  timezone: Timezone;
  timeline?: {
    stepSizeInMins?: number;
    pixelsPerMinute?: number;
    hourIncrement?: PracticeTimelineHourIncrement;
    tooltipDelay?: PracticeTimelineTooltipDelay;
  };
  eventColourOverrides?: {
    [key in EventType]?: string;
  };
  statusIndicatorOverrides?: {
    [key in StatusIndicatorType]?: Partial<IStatusIndicatorConfig>;
  };
  deadzoneColourOverride?: string;
  patientPortal?: {
    staffOnlineOrder?: IStaffOnlineOrder;
    bookingWindow?: IBookingWindow;
  };
  print?: {
    prescriptionMarginOffset?: IPrintPrescriptionSettings;
    documentSettings?: IDocumentSettings;
  };
  sterilisation?: {
    barcodeModeEnabled?: boolean;
  };
  defaultFeeSchedule?: DocumentReference<IFeeSchedule>;
  accounting?: {
    /**
     * When true, deposits are counted toward practitioner allocation limits.
     * By default this is false, meaning deposits are excluded from allocation.
     * Useful for practices that pay practitioners up front for account credits.
     */
    includeDepositsInAllocations?: boolean;
    /**
     * Number of days from issue date until an invoice is due.
     * Defaults to DEFAULT_INVOICE_DUE_DATE_DAYS (14) when not set.
     * Set to 0 for invoices due immediately on issue.
     */
    invoiceDueDateDays?: number;
    practitionerIncome?: {
      labJobTimestamp?: LabJobIncomeTimestampField;
    };
  };
  referral?: {
    emailTemplateDefaults?: {
      initialSend?: {
        templateRef?: DocumentReference<ITemplateDefinition>;
        subject?: string;
      };
      updateNotification?: {
        templateRef?: DocumentReference<ITemplateDefinition>;
        subject?: string;
      };
      patientLetter?: {
        templateRef?: DocumentReference<ITemplateDefinition>;
        subject?: string;
      };
    };
  };
  aiReceptionist?: IAIReceptionistConfig;
  aiScribe?: {
    /** When true, the AI Scribe dashboard renders generated demo data (metrics + sessions) so the feature can be demonstrated without real sessions. No longer gates management tooling — that is gated by management access. */
    testModeEnabled?: boolean;
  };
}

export interface IAIReceptionistConfig {
  enabled: boolean;
  /** When true, Ovie API calls for this practice are mocked instead of hitting the live service. Sole source of truth — there is no environment-level fallback; defaults to off (live) when unset. Resolved via PracticeSettings.isAiReceptionistTestModeEnabled. */
  testModeEnabled?: boolean;
  analyticsConfig?: IAIReceptionistAnalyticsConfig;
  demoBooking?: {
    requestedAt: Timestamp;
    eventUri?: string;
    inviteeUri?: string;
  };
}

export enum StaffOnlineOrderOption {
  Alphabetically = 'alphabetically',
  Availability = 'availability',
  NextAvailable = 'nextAvailable',
  Custom = 'custom',
}

export interface IStaffOnlineOrder {
  orderBy: StaffOnlineOrderOption;
  customOrder: DocumentReference<IStaffer>[];
}

export interface ITyroTerminal extends ISoftDelete {
  name: string;
  mid: number;
  tid: number;
  practitionerRef?: DocumentReference<IStaffer>;
  lastPaired?: Timestamp;
  integrationKey?: string;
}

export interface ISmartpayTerminal extends ISoftDelete {
  name: string;
  uid: string;
  lastPaired?: Timestamp;
}

export interface IVerifoneTerminal extends ISoftDelete {
  name: string;
  uid: string;
  serialNumber: string;
  lastPaired?: Timestamp;
}

export interface IPracticeTyroSettings {
  isEnabled: boolean;
  iClient: string; // TODO: use TyroIClientUrl (currently not available for import)
  addCDBSAsPending: boolean;
}

export interface IUserRestrictionDay {
  day: DayOfWeek;
  allowedFrom: Time24hrType;
  allowedTo: Time24hrType;
}

export interface IPracticeUserRestriction {
  user: INamedDocument<IUser>;
  schedule: IUserRestrictionDay[];
  ipWhitelist?: IPracticeIpWhitelist;
}

export interface IPracticeUserRestrictions {
  isEnabled: boolean;
  restrictions: IPracticeUserRestriction[];
}

export interface IPracticeIpWhitelist {
  isEnabled: boolean;
  whitelist: string[];
}

export interface IPracticeRestrictions {
  ipWhitelist?: IPracticeIpWhitelist;
  ipExemptUsers?: INamedDocument<IUser>[];
  userRestrictions?: IPracticeUserRestrictions;
}

export interface IPracticeSmartpaySettings {
  isEnabled: boolean;
}

export enum VerifoneReceiptPrintMethod {
  Terminal = 'terminal',
  Principle = 'principle',
}

export interface IPracticeVerifoneSettings {
  isEnabled: boolean;
  testModeEnabled?: boolean;
  /** Who prints the paper receipt. Absent means Terminal. */
  receiptPrintMethod?: VerifoneReceiptPrintMethod;
}

export interface IPracticeMohSettings {
  claimFormEmail: string;
  preApprovalFormEmail: string;
  enrolmentFormEmail?: string;
  districtArea?: string;
  agreementNumber?: string;
  payeeNumber?: string;
  agreementHolderName?: string;
  agreementHolderSource?: MohAgreementHolderSource;
  ccEmail?: string;
  bccEmail?: string;
  testModeEnabled?: boolean;
  testEmail?: string;
}

export enum RequiredMohPracticeSetting {
  ClaimFormEmail = 'claimFormEmail',
  PreApprovalFormEmail = 'preApprovalFormEmail',
  PayeeNumber = 'payeeNumber',
  AgreementNumber = 'agreementNumber',
}

export const REQUIRED_MOH_PRACTICE_SETTING_LABEL_MAP: Record<
  RequiredMohPracticeSetting,
  string
> = {
  [RequiredMohPracticeSetting.ClaimFormEmail]: 'Claim Form Email',
  [RequiredMohPracticeSetting.PreApprovalFormEmail]:
    'Prior Approval Form Email',
  [RequiredMohPracticeSetting.PayeeNumber]: 'Payee Number',
  [RequiredMohPracticeSetting.AgreementNumber]: 'Agreement Number',
};

export interface IPracticeAccSettings {
  enabled: boolean;
  vendorId: string;
  pfxPassphrase: string;
  pfxFilePath: string;
  providerId?: string;
  vendorGST?: string;
  testMode?: boolean;
  facilityId?: string;
  formNotificationSmsTemplate?: DocumentReference<ITemplateDefinition>;
  formNotificationEmailTemplate?: DocumentReference<ITemplateDefinition>;
  addACCAsPending?: boolean;
}

export interface IBookingWindow {
  min?: IBookingWindowTiming;
  max?: IBookingWindowTiming;
}

export interface IBookingWindowTiming {
  unit: OnlineBookingTimingUnit;
  amount: number;
}

export enum BookingWindowType {
  Min = 'min',
  Max = 'max',
}

export enum OnlineBookingTimingUnit {
  Days = 'days',
  Weeks = 'weeks',
  Months = 'months',
}

export type IPracticeMetadata = Partial<ISystemPracticeMetadata> &
  Partial<IHeidiPracticeMetadata>;

export interface ISystemPracticeMetadata {
  address: IAddressMetadata;
  clickUpTaskId?: string;
}

export interface IHeidiPracticeMetadata {
  heidi: {
    email?: string;
    testMode?: boolean;
  };
}

export interface IPractice extends IFirestoreModel {
  name: string;
  slug: string;
  address: string;
  phone: string;
  email: string;
  websiteUrl?: string;
  settings: IPracticeSettings;
  tyroSettings?: IPracticeTyroSettings;
  smartpaySettings?: IPracticeSmartpaySettings;
  verifoneSettings?: IPracticeVerifoneSettings;
  hicapsConnectSettings?: IPracticeHicapsConnectSettings;
  schedule: IRoster;
  yearOpened: number;
  billingEntities?: IBillingEntity[];
  restrictions?: IPracticeRestrictions;
  enabledForSchedulingAggregateTrial?: boolean;
  mohSettings?: IPracticeMohSettings;
  accSettings?: IPracticeAccSettings;
  metadata?: IPracticeMetadata;
  branding?: IBrandingSettings;
}

export function isPractice(data: unknown): data is IPractice {
  return (
    isObject(data) &&
    isString(data.name) &&
    isString(data.slug) &&
    isString(data.address) &&
    isString(data.phone) &&
    isString(data.email)
  );
}

export interface IBrandingSettings {
  logoUrl?: string;
}

export interface IPracticeHicapsConnectSettings {
  isEnabled?: boolean;
  testModeEnabled?: boolean;
  addCDBSAsPending?: boolean;
  useV2Terminals?: boolean;
}

export interface IHicapsConnectTerminal extends ISoftDelete {
  name: string;
  merchantId: string;
  terminalId: string;
  bridgeDevice: DocumentReference<IBridgeDevice>;
  practitionerRef?: DocumentReference<IStaffer>;
  lastActive?: Timestamp;
}

/**
 * A one-directional "this terminal has been seen related to X" edge.
 *
 * `stale` and `disabled` are orthogonal: `stale` is sync-owned system state,
 * `disabled` is user intent that sync never touches. Whether an edge may be
 * used goes through the per-edge-type predicates, because the two edge types
 * disagree on `stale`.
 */
export interface IHicapsConnectEdge<T extends object> {
  ref: DocumentReference<T>;
  lastSeen: Timestamp;
  /** sync-owned: source no longer reports this edge */
  stale?: boolean;
  /** user-set: never use this edge; sync never touches */
  disabled?: boolean;
}

export interface IHicapsConnectBridgeEdge
  extends IHicapsConnectEdge<IBridgeDevice> {
  /** verbatim from that bridge; sent unchanged to HICAPS */
  serverUrl: string;
  /** serial attachment (numbered COM port, not COMT) — that bridge owns the cable */
  isLocal: boolean;
}

/** Terminal-level `disabled` and `lastActive` are derived, not stored. */
export interface IHicapsConnectTerminalV2 extends ISoftDelete {
  /** bare TID, e.g. "SE102A" */
  terminalId: string;
  /** user-set; sync only assigns a default on create */
  name: string;
  /**
   * user-set; sync never touches. A ref rather than a bridge doc id because
   * this is a standalone user-set pointer to a bridge device, not a map key,
   * so it carries the reference directly.
   */
  preferredBridge?: DocumentReference<IBridgeDevice>;
  /** keyed by bridge device doc id (immutable) */
  bridges: Record<string, IHicapsConnectBridgeEdge>;
  /** keyed by merchantId */
  merchants: Record<string, IHicapsConnectEdge<IHicapsConnectMerchant>>;
}

export interface IHicapsConnectMerchant extends ISoftDelete {
  merchantId: string;
  /** from HICAPS, refreshed by sync */
  providerName: string;
  /** user-set nickname; sync never touches */
  name?: string;
  /** user-set; sync never touches */
  practitionerRef?: DocumentReference<IStaffer>;
  /** user-set; excluded from terminal selection */
  disabled?: boolean;
}
